import React, { useState, useEffect } from 'react';
import { infrastructureApi } from '../services/infrastructureApi';
import { useDraggable } from '../../../components/ui/useDraggable';
import {
  DiscoveryResult,
  DiscoveredRegistry,
  DiscoveredKeyVault,
  DiscoverySelection,
  DiscoveredSubscription,
  DiscoveredResourceGroup,
} from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onApply: (selection: DiscoverySelection) => void;
}

type Step = 'discover' | 'select' | 'confirm';

export const DiscoverFromAzureModal: React.FC<Props> = ({ isOpen, onClose, onApply }) => {
  const [step, setStep] = useState<Step>('discover');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [resourceGroup, setResourceGroup] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DiscoveryResult | null>(null);
  const [selectedRegistry, setSelectedRegistry] = useState<DiscoveredRegistry | null>(null);
  const [selectedKeyVault, setSelectedKeyVault] = useState<DiscoveredKeyVault | null>(null);
  const [aciResourceGroup, setAciResourceGroup] = useState('');
  const [aciSubscriptionId, setAciSubscriptionId] = useState('');

  // Subscription + resource group dropdown data
  const [subscriptions, setSubscriptions] = useState<DiscoveredSubscription[]>([]);
  const [resourceGroups, setResourceGroups] = useState<DiscoveredResourceGroup[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [loadingResourceGroups, setLoadingResourceGroups] = useState(false);
  const { transform, onHandleMouseDown } = useDraggable({ handleSelector: '[data-modal-drag-handle]' });

  // Load subscriptions when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setLoadingSubscriptions(true);
    infrastructureApi.listSubscriptions()
      .then(subs => setSubscriptions(subs))
      .catch(() => setSubscriptions([]))
      .finally(() => setLoadingSubscriptions(false));
  }, [isOpen]);

  // Load resource groups when subscription changes
  useEffect(() => {
    if (!subscriptionId) {
      setResourceGroups([]);
      setResourceGroup('');
      return;
    }
    setLoadingResourceGroups(true);
    setResourceGroup('');
    infrastructureApi.listResourceGroups(subscriptionId)
      .then(rgs => setResourceGroups(rgs))
      .catch(() => setResourceGroups([]))
      .finally(() => setLoadingResourceGroups(false));
  }, [subscriptionId]);

  if (!isOpen) return null;

  const handleDiscover = async () => {
    if (!subscriptionId) {
      setError('Please select a subscription');
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const data = await infrastructureApi.discoverResources(subscriptionId, resourceGroup || undefined);
      setResult(data);
      setSelectedRegistry(data.registries[0] ?? null);
      setSelectedKeyVault(data.keyVaults[0] ?? null);
      const firstRg = data.registries[0]?.resourceGroup ?? data.keyVaults[0]?.resourceGroup ?? resourceGroup ?? '';
      setAciResourceGroup(firstRg);
      setAciSubscriptionId(subscriptionId);
      setStep('select');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    onApply({
      registry: selectedRegistry,
      keyVault: selectedKeyVault,
      aciResourceGroup,
      aciSubscriptionId,
    });
    handleClose();
  };

  const handleClose = () => {
    setStep('discover');
    setSubscriptionId('');
    setResourceGroup('');
    setResourceGroups([]);
    setResult(null);
    setError(null);
    setSelectedRegistry(null);
    setSelectedKeyVault(null);
    setAciResourceGroup('');
    setAciSubscriptionId('');
    onClose();
  };

  const resourceGroupOptions = Array.from(new Set([
    ...(result?.registries.map(r => r.resourceGroup) ?? []),
    ...(result?.keyVaults.map(v => v.resourceGroup) ?? []),
  ])).filter(Boolean);

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen px-4'>
        {/* Backdrop — transparent + non-dismissing (Spec 197) */}
        <div className='fixed inset-0 bg-transparent' />

        {/* Modal */}
        <div
          onMouseDown={onHandleMouseDown}
          style={{ transform }}
          className='relative bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] shadow-2xl w-full max-w-2xl'
        >
          {/* Header */}
          <div data-modal-drag-handle className='px-6 py-4 border-b border-[var(--color-border)] cursor-move select-none'>
            <div className='flex items-center justify-between'>
              <div>
                <h2 className='text-lg font-semibold text-[var(--color-text-primary)]'>Discover from Azure</h2>
                <p className='text-sm text-[var(--color-text-secondary)] mt-0.5'>
                  {step === 'discover' && 'Enter a subscription to scan for existing resources'}
                  {step === 'select' && 'Select the resources to use for this environment'}
                  {step === 'confirm' && 'Review and confirm before applying to the forms'}
                </p>
              </div>
              <button onClick={handleClose} className='text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'>
                <svg className='h-5 w-5' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
            {/* Step indicator */}
            <div className='flex items-center mt-3 space-x-2'>
              {(['discover', 'select', 'confirm'] as Step[]).map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                    step === s ? 'bg-blue-600 text-white' :
                    (['discover', 'select', 'confirm'].indexOf(step) > i) ? 'bg-green-500 text-white' :
                    'bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)]'
                  }`}>
                    {(['discover', 'select', 'confirm'].indexOf(step) > i) ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs capitalize ${step === s ? 'text-[var(--color-accent)] font-medium' : 'text-[var(--color-text-secondary)]'}`}>
                    {s}
                  </span>
                  {i < 2 && <div className='flex-1 h-px bg-[var(--color-bg-subtle)] max-w-[40px]' />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className='px-6 py-5'>

            {/* Step 1: Discover */}
            {step === 'discover' && (
              <div className='space-y-4'>
                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
                    Subscription <span className='text-red-500'>*</span>
                  </label>
                  {loadingSubscriptions ? (
                    <div className='flex items-center space-x-2 text-sm text-[var(--color-text-secondary)] py-2'>
                      <svg className='animate-spin h-4 w-4' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                      </svg>
                      <span>Loading subscriptions...</span>
                    </div>
                  ) : (
                    <select
                      value={subscriptionId}
                      onChange={e => setSubscriptionId(e.target.value)}
                      className='w-full px-3 py-2 border border-[var(--color-border)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--color-bg-card)] text-[var(--color-text-primary)]'
                    >
                      <option value=''>— select a subscription —</option>
                      {subscriptions.map(s => (
                        <option key={s.subscriptionId} value={s.subscriptionId}>
                          {s.displayName} ({s.subscriptionId})
                        </option>
                      ))}
                    </select>
                  )}
                  {!loadingSubscriptions && subscriptions.length === 0 && (
                    <p className='text-xs text-[var(--color-text-secondary)] mt-1'>No subscriptions found — the current identity may not have access.</p>
                  )}
                </div>
                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
                    Resource Group{' '}
                    <span className='text-[var(--color-text-secondary)] font-normal'>(optional — leave blank to scan all)</span>
                  </label>
                  {loadingResourceGroups ? (
                    <div className='flex items-center space-x-2 text-sm text-[var(--color-text-secondary)] py-2'>
                      <svg className='animate-spin h-4 w-4' fill='none' viewBox='0 0 24 24'>
                        <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                        <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                      </svg>
                      <span>Loading resource groups...</span>
                    </div>
                  ) : (
                    <select
                      value={resourceGroup}
                      onChange={e => setResourceGroup(e.target.value)}
                      disabled={!subscriptionId}
                      className='w-full px-3 py-2 border border-[var(--color-border)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--color-bg-card)] text-[var(--color-text-primary)] disabled:bg-[var(--color-bg-subtle)] disabled:text-[var(--color-text-secondary)]'
                    >
                      <option value=''>— all resource groups —</option>
                      {resourceGroups.map(rg => (
                        <option key={rg.name} value={rg.name}>
                          {rg.name} ({rg.location})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                {error && (
                  <p className='text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2'>{error}</p>
                )}
              </div>
            )}

            {/* Step 2: Select */}
            {step === 'select' && result && (
              <div className='space-y-6'>
                {result.errors.length > 0 && (
                  <div className='bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3'>
                    <p className='text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1'>Some resources could not be scanned:</p>
                    {result.errors.map(e => (
                      <p key={e.source} className='text-xs text-yellow-700 dark:text-yellow-400'>{e.source}: {e.message}</p>
                    ))}
                  </div>
                )}

                {/* ACR selection */}
                <div>
                  <h3 className='text-sm font-semibold text-[var(--color-text-primary)] mb-2 flex items-center'>
                    <span className='mr-2'>📦</span> Container Registry
                  </h3>
                  {result.registries.length === 0 ? (
                    <p className='text-sm text-[var(--color-text-secondary)] italic'>No registries found in this subscription</p>
                  ) : (
                    <div className='space-y-2'>
                      {result.registries.map(reg => (
                        <label key={reg.name} className={`flex items-start p-3 border rounded-lg cursor-pointer ${
                          selectedRegistry?.name === reg.name ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-[var(--color-border)] hover:border-[var(--color-border)]'
                        }`}>
                          <input
                            type='radio'
                            name='registry'
                            className='mt-0.5 mr-3'
                            checked={selectedRegistry?.name === reg.name}
                            onChange={() => setSelectedRegistry(reg)}
                          />
                          <div>
                            <p className='text-sm font-medium text-[var(--color-text-primary)]'>{reg.name}</p>
                            <p className='text-xs text-[var(--color-text-secondary)]'>{reg.loginServer} · {reg.resourceGroup} · {reg.location}{reg.sku ? ` · ${reg.sku}` : ''}</p>
                          </div>
                        </label>
                      ))}
                      <label className={`flex items-start p-3 border rounded-lg cursor-pointer ${
                        selectedRegistry === null ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-[var(--color-border)] hover:border-[var(--color-border)]'
                      }`}>
                        <input
                          type='radio'
                          name='registry'
                          className='mt-0.5 mr-3'
                          checked={selectedRegistry === null}
                          onChange={() => setSelectedRegistry(null)}
                        />
                        <p className='text-sm text-[var(--color-text-secondary)] italic'>Skip — don't set ACR config</p>
                      </label>
                    </div>
                  )}
                </div>

                {/* Key Vault selection */}
                <div>
                  <h3 className='text-sm font-semibold text-[var(--color-text-primary)] mb-2 flex items-center'>
                    <span className='mr-2'>🔐</span> Key Vault
                  </h3>
                  {result.keyVaults.length === 0 ? (
                    <p className='text-sm text-[var(--color-text-secondary)] italic'>No Key Vaults found in this subscription</p>
                  ) : (
                    <div className='space-y-2'>
                      {result.keyVaults.map(kv => (
                        <label key={kv.name} className={`flex items-start p-3 border rounded-lg cursor-pointer ${
                          selectedKeyVault?.name === kv.name ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-[var(--color-border)] hover:border-[var(--color-border)]'
                        }`}>
                          <input
                            type='radio'
                            name='keyvault'
                            className='mt-0.5 mr-3'
                            checked={selectedKeyVault?.name === kv.name}
                            onChange={() => setSelectedKeyVault(kv)}
                          />
                          <div>
                            <p className='text-sm font-medium text-[var(--color-text-primary)]'>{kv.name}</p>
                            <p className='text-xs text-[var(--color-text-secondary)]'>{kv.vaultUri} · {kv.resourceGroup} · {kv.location}</p>
                          </div>
                        </label>
                      ))}
                      <label className={`flex items-start p-3 border rounded-lg cursor-pointer ${
                        selectedKeyVault === null ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-[var(--color-border)] hover:border-[var(--color-border)]'
                      }`}>
                        <input
                          type='radio'
                          name='keyvault'
                          className='mt-0.5 mr-3'
                          checked={selectedKeyVault === null}
                          onChange={() => setSelectedKeyVault(null)}
                        />
                        <p className='text-sm text-[var(--color-text-secondary)] italic'>Skip — don't set Key Vault config</p>
                      </label>
                    </div>
                  )}
                </div>

                {/* ACI resource group */}
                <div>
                  <h3 className='text-sm font-semibold text-[var(--color-text-primary)] mb-2 flex items-center'>
                    <span className='mr-2'>🖥️</span> Container Instances Resource Group
                  </h3>
                  <div className='space-y-2'>
                    <select
                      value={aciResourceGroup}
                      onChange={e => setAciResourceGroup(e.target.value)}
                      className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    >
                      <option value=''>— skip ACI resource group —</option>
                      {resourceGroupOptions.map(rg => (
                        <option key={rg} value={rg}>{rg}</option>
                      ))}
                    </select>
                    <input
                      type='text'
                      value={aciResourceGroup}
                      onChange={e => setAciResourceGroup(e.target.value)}
                      placeholder='Or type a custom resource group name'
                      className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {step === 'confirm' && (
              <div className='space-y-4'>
                <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                  <p className='text-sm font-medium text-blue-800 dark:text-blue-300 mb-3'>
                    The following values will be pre-filled into the configuration forms. No data will be saved until you click "Save Configuration" on each tab.
                  </p>
                  <table className='w-full text-sm'>
                    <tbody className='divide-y divide-blue-100 dark:divide-blue-900/40'>
                      {selectedRegistry && (
                        <>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium w-48'>ACR Registry Name</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300'>{selectedRegistry.name}</td>
                          </tr>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>ACR Resource Group</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300'>{selectedRegistry.resourceGroup}</td>
                          </tr>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>ACR Subscription ID</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300 text-xs'>{subscriptionId}</td>
                          </tr>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>ACR Login Server</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300 text-xs'>{selectedRegistry.loginServer}</td>
                          </tr>
                        </>
                      )}
                      {selectedKeyVault && (
                        <tr>
                          <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>Key Vault URL</td>
                          <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300 text-xs'>{selectedKeyVault.vaultUri}</td>
                        </tr>
                      )}
                      {aciResourceGroup && (
                        <>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>ACI Resource Group</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300'>{aciResourceGroup}</td>
                          </tr>
                          <tr>
                            <td className='py-1.5 pr-3 text-blue-700 dark:text-blue-400 font-medium'>ACI Subscription ID</td>
                            <td className='py-1.5 font-mono text-blue-900 dark:text-blue-300 text-xs'>{aciSubscriptionId}</td>
                          </tr>
                        </>
                      )}
                      {!selectedRegistry && !selectedKeyVault && !aciResourceGroup && (
                        <tr>
                          <td colSpan={2} className='py-2 text-blue-600 dark:text-blue-400 italic'>Nothing selected — no changes will be applied</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className='text-xs text-[var(--color-text-secondary)]'>
                  Existing form values will be overwritten by the selections above. ACI defaults (CPU, memory, timeout) are not changed by discovery.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className='px-6 py-4 border-t border-[var(--color-border)] flex justify-between'>
            <button
              onClick={handleClose}
              className='px-4 py-2 text-sm text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)]'
            >
              Cancel
            </button>
            <div className='flex space-x-3'>
              {step === 'select' && (
                <button
                  onClick={() => setStep('discover')}
                  className='px-4 py-2 text-sm text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)]'
                >
                  Back
                </button>
              )}
              {step === 'confirm' && (
                <button
                  onClick={() => setStep('select')}
                  className='px-4 py-2 text-sm text-[var(--color-text-secondary)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)]'
                >
                  Back
                </button>
              )}
              {step === 'discover' && (
                <button
                  onClick={handleDiscover}
                  disabled={isLoading || !subscriptionId}
                  className='px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2'
                >
                  {isLoading && (
                    <svg className='animate-spin h-4 w-4' fill='none' viewBox='0 0 24 24'>
                      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                      <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                    </svg>
                  )}
                  <span>{isLoading ? 'Discovering...' : 'Discover Resources'}</span>
                </button>
              )}
              {step === 'select' && (
                <button
                  onClick={() => setStep('confirm')}
                  className='px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700'
                >
                  Review
                </button>
              )}
              {step === 'confirm' && (
                <button
                  onClick={handleConfirm}
                  className='px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700'
                >
                  Apply to Forms
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoverFromAzureModal;
